import { ShadowUpdateAccepted, ThingShadow } from '@cco/model';
import { createReducer, on } from '@ngrx/store';
import * as lodash from 'lodash';
import * as IotThingActions from './iot-thing.actions';

/**
 * Recursively remove null and empty elements from an object
 *
 * Based on https://stackoverflow.com/a/44808210
 */
function cleanDeep<T>(obj: T): T {
  function internalClean(obj1: any) {
    return lodash.transform(obj1, (result, value, key) => {
      const isCollection = lodash.isObject(value);
      const cleaned = isCollection ? internalClean(value) : value;

      if (isCollection && lodash.isEmpty(cleaned)) {
        return;
      }
      if (lodash.isNull(cleaned)) {
        return;
      }

      if (lodash.isArray(result))
       result.push(cleaned);
      else
       ((result as any)[key] = cleaned);
    });
  }

  return lodash.isObject(obj) ? internalClean(obj) as T : (obj as T);
}

export interface State {
  // -eslint-disable-next-line @typescript-eslint/ban-types
  [thingName: string]: ThingShadow<object>;
}

const applyShadowUpdate = (
  state: State,
  update: { thingName: string; shadowUpdateAccepted: ShadowUpdateAccepted<object> },
): State => {
  // We can't patch what isn't there
  if (!state[update.thingName]) {
    return state;
  }

  const shadow = typeof structuredClone === 'function' ? structuredClone(state[update.thingName]) : lodash.cloneDeep(state[update.thingName]);
  const shadowUpdate = {
    ...lodash.omitBy(
      {
        state: update.shadowUpdateAccepted.state,
        metadata: update.shadowUpdateAccepted.metadata,
      },
      lodash.isNil,
    ),
    timestamp: update.shadowUpdateAccepted.timestamp,
    version: update.shadowUpdateAccepted.version,
  } as ThingShadow<Record<string, unknown>>;

  if (!shadow?.version) {
    // We do not have a current full shadow.  Ignoring update.
    return state;
  }

  if (shadowUpdate.version < shadow.version + 1) {
    return state;
  }

  if (shadowUpdate.version > shadow.version + 1) {
    return state;
  }
  const newShadow = cleanDeep(
    lodash.mergeWith(shadow, shadowUpdate, (objValue, srcValue) => {
      // Overwrite arrays
      if (Array.isArray(srcValue)) {
        return srcValue;
      }

      // This needs to return undefined so mergeWith uses its default behavior.
      // Returning null will override that behavior.
      return;
    }),
  ) as ThingShadow<Record<string, unknown>>;

  return { ...state, [update.thingName]: newShadow };
};

export const reducer = createReducer<State>(
  {},
  on(IotThingActions.patchThingShadow, (state, action): State =>
    applyShadowUpdate(state, action)),
  on(IotThingActions.patchThingShadows, (state, action): State =>
    action.updates.reduce(applyShadowUpdate, state)),
  on(IotThingActions.upsertThingShadow, (state, action): State => {
    const storeVersion = state[action.thingName]?.version;
    if (storeVersion && storeVersion > action.shadow.version) {
      // API data may be delayed; don't overwrite if the update is stale
      return state;
    }
    return { ...state, [action.thingName]: action.shadow };
  }),
);
